import { COBRO_GENERADO_POR, type CobroGeneradoPor } from './types';
import { calculateReissueWindow, isInReissueWindow } from './operational-period-window';

export const INVOICE_STATUS = {
  VIGENTE: 'VIGENTE',
  PAGADA: 'PAGADA',
  ANULADA: 'ANULADA',
} as const;

export type InvoiceStatus = (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

/** Legacy catalog key accepted at the boundary while ANULADA is canonical. */
export const INVOICE_STATUS_LEGACY = {
  ANULADO: 'ANULADO',
} as const;

export const INVOICE_LIFECYCLE_TRIGGER = {
  CREATE: 'CREATE',
  PAYMENT_OR_CREDIT: 'PAYMENT_OR_CREDIT',
  PAYMENT_ANNULMENT: 'PAYMENT_ANNULMENT',
  ADJUSTMENT_APPLY: 'ADJUSTMENT_APPLY',
  ADJUSTMENT_REVERSE: 'ADJUSTMENT_REVERSE',
  INVOICE_ANNULMENT: 'INVOICE_ANNULMENT',
  IDEMPOTENT_RETRY: 'IDEMPOTENT_RETRY',
  STATE_RECONCILIATION: 'STATE_RECONCILIATION',
} as const;

export type InvoiceLifecycleTrigger =
  (typeof INVOICE_LIFECYCLE_TRIGGER)[keyof typeof INVOICE_LIFECYCLE_TRIGGER];

export const INVOICE_ACTION = {
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  ANNUL: 'ANNUL',
  REISSUE: 'REISSUE',
} as const;

export type InvoiceAction = (typeof INVOICE_ACTION)[keyof typeof INVOICE_ACTION];

export interface InvoiceStateTransitionInput {
  current: InvoiceStatus | null;
  trigger: InvoiceLifecycleTrigger;
  canonicalRemainderIsZero: boolean;
  sameOperationRetry?: boolean;
}

export interface InvoiceStateTransitionDecision {
  ok: boolean;
  nextState: InvoiceStatus;
  changed: boolean;
  idempotent: boolean;
  reason: string | null;
}

export interface InvoiceActionAvailability {
  visible: boolean;
  enabled: boolean;
}

export interface InvoiceActionPolicy {
  edit: InvoiceActionAvailability;
  delete: InvoiceActionAvailability;
  annul: InvoiceActionAvailability;
}

export interface InvoiceActionPolicyInput {
  status: InvoiceStatus | null;
  origin: CobroGeneradoPor | null | undefined;
}

function success(
  nextState: InvoiceStatus,
  changed: boolean,
  idempotent = false,
): InvoiceStateTransitionDecision {
  return { ok: true, nextState, changed, idempotent, reason: null };
}

function failure(current: InvoiceStatus | null, reason: string): InvoiceStateTransitionDecision {
  return {
    ok: false,
    nextState: current ?? INVOICE_STATUS.VIGENTE,
    changed: false,
    idempotent: false,
    reason,
  };
}

/**
 * Pure invoice lifecycle rules. The caller supplies the canonical remainder
 * result; this module never calculates money or reads persistence.
 */
export function transitionInvoiceState(
  input: InvoiceStateTransitionInput,
): InvoiceStateTransitionDecision {
  const retry =
    input.sameOperationRetry === true ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.IDEMPOTENT_RETRY;
  if (retry) {
    if (input.current === null) {
      return failure(null, 'An idempotent retry requires an existing invoice state');
    }
    return success(input.current, false, true);
  }

  if (input.current === INVOICE_STATUS.ANULADA) {
    return failure(INVOICE_STATUS.ANULADA, 'ANULADA is terminal');
  }

  if (input.trigger === INVOICE_LIFECYCLE_TRIGGER.INVOICE_ANNULMENT) {
    return success(INVOICE_STATUS.ANULADA, true);
  }

  if (input.trigger === INVOICE_LIFECYCLE_TRIGGER.CREATE && input.current === null) {
    return success(
      input.canonicalRemainderIsZero ? INVOICE_STATUS.PAGADA : INVOICE_STATUS.VIGENTE,
      true,
    );
  }

  if (input.current === null) {
    return failure(null, 'A non-create transition requires an existing invoice state');
  }

  const isSupportedTrigger =
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.CREATE ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.PAYMENT_OR_CREDIT ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.PAYMENT_ANNULMENT ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.ADJUSTMENT_APPLY ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.ADJUSTMENT_REVERSE ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.STATE_RECONCILIATION;
  if (!isSupportedTrigger) {
    return failure(input.current, `Unsupported invoice transition trigger: ${input.trigger}`);
  }

  const nextState = input.canonicalRemainderIsZero ? INVOICE_STATUS.PAGADA : INVOICE_STATUS.VIGENTE;
  const canReopenPaid =
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.PAYMENT_ANNULMENT ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.ADJUSTMENT_REVERSE ||
    input.trigger === INVOICE_LIFECYCLE_TRIGGER.STATE_RECONCILIATION;
  if (
    input.current === INVOICE_STATUS.PAGADA &&
    nextState === INVOICE_STATUS.VIGENTE &&
    !canReopenPaid
  ) {
    return failure(
      input.current,
      'A paid invoice can reopen only after a payment or adjustment reversal',
    );
  }

  return success(nextState, nextState !== input.current);
}

export function normalizeInvoiceStatusKey(value: string | null | undefined): InvoiceStatus | null {
  if (value === INVOICE_STATUS.VIGENTE) return INVOICE_STATUS.VIGENTE;
  if (value === INVOICE_STATUS.PAGADA) return INVOICE_STATUS.PAGADA;
  if (value === INVOICE_STATUS.ANULADA || value === INVOICE_STATUS_LEGACY.ANULADO) {
    return INVOICE_STATUS.ANULADA;
  }
  return null;
}

/**
 * Shared presentation policy. Delete is never available because financial
 * invoices are append-only; the backend still rejects direct DELETE requests.
 */
export function getInvoiceActionPolicy(input: InvoiceActionPolicyInput): InvoiceActionPolicy {
  const terminal = input.status === INVOICE_STATUS.ANULADA;
  const editable =
    input.status === INVOICE_STATUS.VIGENTE &&
    (input.origin === COBRO_GENERADO_POR.MANUAL || input.origin == null);

  return {
    edit: { visible: editable, enabled: editable },
    delete: { visible: false, enabled: false },
    annul: { visible: input.status !== null, enabled: input.status !== null && !terminal },
  };
}

/**
 * Display-only gate for the re-issue action (spec IR-W1 FE + design D8). The
 * authoritative window re-check is the backend IR-W2 guard; this function only
 * decides whether to render/enable the menu entry. It composes the shared
 * window math (never duplicated). True IFF: the invoice is VIGENTE, not linked
 * to a prepaid package, the client has a `diaCobro`, the invoice has a
 * `fechaInicio`, and that start falls inside the re-issue window.
 */
export function canReissue(
  invoice: {
    id: string;
    estado: InvoiceStatus | null;
    fechaInicio: string | null;
    linkedToPrepaid: boolean;
  },
  client: { diaCobro: number | null },
  fechaBase: string,
): boolean {
  if (invoice.estado !== INVOICE_STATUS.VIGENTE) return false;
  if (invoice.linkedToPrepaid) return false;
  if (client.diaCobro === null) return false;
  if (invoice.fechaInicio === null) return false;
  return isInReissueWindow(invoice.fechaInicio, calculateReissueWindow(client.diaCobro, fechaBase));
}
